export interface City {
  id: string;
  name: string;
  slug: string;
  state: string;
  stateCode: string;
  region: string;
  description: string;
  population?: string;
  businessCount?: string;
}

export interface FundingType {
  id: string;
  name: string;
  slug: string;
  shortDescription: string;
  icon: string;
  minAmount: number;
  maxAmount: number;
  typicalTerm: string;
  range: string;
  timeline: string;
}

export const CITIES: City[] = [
  {
    id: 'new-york',
    name: 'New York',
    slug: 'new-york',
    state: 'New York',
    stateCode: 'NY',
    region: 'Northeast',
    description: 'The financial capital of the world with diverse business opportunities across all sectors.',
    population: '8.3M+',
    businessCount: '200K+'
  },
  {
    id: 'miami',
    name: 'Miami',
    slug: 'miami',
    state: 'Florida',
    stateCode: 'FL',
    region: 'Southeast',
    description: 'A vibrant international business hub with strong tourism, trade, and finance sectors.',
    population: '470K+',
    businessCount: '50K+'
  },
  {
    id: 'dallas',
    name: 'Dallas',
    slug: 'dallas',
    state: 'Texas',
    stateCode: 'TX',
    region: 'Southwest',
    description: 'A major business center with thriving technology, healthcare, and financial services industries.',
    population: '1.3M+',
    businessCount: '80K+'
  },
  {
    id: 'houston',
    name: 'Houston',
    slug: 'houston',
    state: 'Texas',
    stateCode: 'TX',
    region: 'Southwest',
    description: 'Energy capital of the world with diverse industries including healthcare, aerospace, and manufacturing.',
    population: '2.3M+',
    businessCount: '100K+'
  },
  {
    id: 'los-angeles',
    name: 'Los Angeles',
    slug: 'los-angeles',
    state: 'California',
    stateCode: 'CA',
    region: 'West',
    description: 'Entertainment capital with robust technology, manufacturing, and international trade sectors.',
    population: '4M+',
    businessCount: '150K+'
  },
  {
    id: 'chicago',
    name: 'Chicago',
    slug: 'chicago',
    state: 'Illinois',
    stateCode: 'IL',
    region: 'Midwest',
    description: 'Major financial and transportation hub with strong manufacturing and professional services sectors.',
    population: '2.7M+',
    businessCount: '120K+'
  },
  {
    id: 'atlanta',
    name: 'Atlanta',
    slug: 'atlanta',
    state: 'Georgia',
    stateCode: 'GA',
    region: 'Southeast',
    description: 'Fast-growing business center with strong logistics, technology, and corporate headquarters presence.',
    population: '500K+',
    businessCount: '60K+'
  },
  {
    id: 'phoenix',
    name: 'Phoenix',
    slug: 'phoenix',
    state: 'Arizona',
    stateCode: 'AZ',
    region: 'Southwest',
    description: 'Rapidly expanding market with growing technology, healthcare, and real estate sectors.',
    population: '1.7M+',
    businessCount: '70K+'
  },
  {
    id: 'las-vegas',
    name: 'Las Vegas',
    slug: 'las-vegas',
    state: 'Nevada',
    stateCode: 'NV',
    region: 'West',
    description: 'Entertainment and hospitality hub with expanding technology and convention business sectors.',
    population: '650K+',
    businessCount: '45K+'
  },
  {
    id: 'tampa',
    name: 'Tampa',
    slug: 'tampa',
    state: 'Florida',
    stateCode: 'FL',
    region: 'Southeast',
    description: 'Growing business center with strong healthcare, finance, and technology industries.',
    population: '400K+',
    businessCount: '40K+'
  }
];

export const FUNDING_TYPES: FundingType[] = [
  {
    id: 'working-capital',
    name: 'Working Capital',
    slug: 'working-capital',
    shortDescription: 'Short-term financing for day-to-day operations, inventory, and cash flow management.',
    icon: 'ri-money-dollar-circle-line',
    minAmount: 10000,
    maxAmount: 500000,
    typicalTerm: '3-18 months',
    range: '$10,000 - $500,000',
    timeline: '1-5 business days'
  },
  {
    id: 'line-of-credit',
    name: 'Line of Credit',
    slug: 'line-of-credit',
    shortDescription: 'Revolving credit line for flexible access to funds as needed for ongoing business expenses.',
    icon: 'ri-bank-card-line',
    minAmount: 10000,
    maxAmount: 250000,
    typicalTerm: '6-24 months',
    range: '$10,000 - $250,000',
    timeline: '3-10 business days'
  },
  {
    id: 'term-loans',
    name: 'Term Loans',
    slug: 'term-loans',
    shortDescription: 'Fixed-amount loans with structured repayment for major investments and growth initiatives.',
    icon: 'ri-hand-coin-line',
    minAmount: 50000,
    maxAmount: 5000000,
    typicalTerm: '1-10 years',
    range: '$50,000 - $5,000,000',
    timeline: '5-14 business days'
  },
  {
    id: 'equipment-financing',
    name: 'Equipment Financing',
    slug: 'equipment-financing',
    shortDescription: 'Asset-backed financing for purchasing or leasing business equipment and machinery.',
    icon: 'ri-tools-line',
    minAmount: 25000,
    maxAmount: 1000000,
    typicalTerm: '1-7 years',
    range: '$25,000 - $1,000,000',
    timeline: '3-7 business days'
  },
  {
    id: 'commercial-real-estate',
    name: 'Commercial Real Estate',
    slug: 'commercial-real-estate',
    shortDescription: 'Long-term financing for purchasing or refinancing commercial property and buildings.',
    icon: 'ri-building-line',
    minAmount: 100000,
    maxAmount: 10000000,
    typicalTerm: '5-25 years',
    range: '$100,000 - $10,000,000+',
    timeline: '60-90 days'
  }
];

export const INDUSTRIES = [
  'Retail',
  'Restaurant & Food Service',
  'Healthcare',
  'Professional Services',
  'Construction',
  'Manufacturing',
  'Technology',
  'Real Estate',
  'Transportation & Logistics',
  'Hospitality',
  'Wholesale Trade',
  'Other'
];

export const FUNDING_PURPOSES = [
  { value: 'working-capital', label: 'Working Capital' },
  { value: 'equipment', label: 'Equipment Purchase' },
  { value: 'expansion', label: 'Business Expansion' },
  { value: 'inventory', label: 'Inventory Purchase' },
  { value: 'real-estate', label: 'Real Estate' },
  { value: 'debt-consolidation', label: 'Debt Consolidation' }
];

export function getCityBySlug(slug: string): City | undefined {
  return CITIES.find(city => city.slug === slug);
}

export function getFundingTypeBySlug(slug: string): FundingType | undefined {
  return FUNDING_TYPES.find(type => type.slug === slug);
}

export function getCityPrequalUrl(citySlug: string): string {
  return `/${citySlug}-prequalification`;
}

export function getCityHubUrl(citySlug: string): string {
  return `/business-funding/${citySlug}`;
}

export function getCityFundingUrl(citySlug: string, fundingSlug: string): string {
  return `/business-funding/${citySlug}/${fundingSlug}`;
}
